import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import AuthServices from '../../services/auth.service';

const USERNAME_PATTERN = /^[a-z0-9_]{3,24}$/;

const UsernameSetupModal = () => {
  const {
    store: { user, accessTokenVerified, status },
    SetUsername,
  } = useAuth();
  const [username, setUsername] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');
  const [availability, setAvailability] = useState(null); // null | 'checking' | 'available' | 'taken'

  const needsUsername =
    accessTokenVerified && Boolean(user?._id) && !String(user?.username || '').trim();

  useEffect(() => {
    if (!needsUsername) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [needsUsername]);

  useEffect(() => {
    if (!needsUsername) return;
    const next = username.trim().toLowerCase();
    if (!USERNAME_PATTERN.test(next)) {
      setAvailability(null);
      return;
    }

    let cancelled = false;
    setAvailability('checking');
    const timer = setTimeout(async () => {
      try {
        const { data } = await AuthServices.checkUsername(next);
        if (cancelled) return;
        setAvailability(data?.data?.available ? 'available' : 'taken');
        if (!data?.data?.available) {
          setLocalError('Username is already taken');
        } else {
          setLocalError('');
        }
      } catch {
        if (!cancelled) setAvailability(null);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [username, needsUsername]);

  if (!needsUsername) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    const next = username.trim().toLowerCase();
    if (!USERNAME_PATTERN.test(next)) {
      setLocalError('Use 3–24 characters: letters, numbers, underscore');
      return;
    }
    if (availability === 'taken') {
      setLocalError('Username is already taken');
      return;
    }
    setLocalError('');
    setBusy(true);
    try {
      await SetUsername({ username: next });
    } catch {
      // toast handled in action
    } finally {
      setBusy(false);
    }
  };

  const statusHint =
    availability === 'checking'
      ? 'Checking…'
      : availability === 'available'
        ? 'Username is available'
        : availability === 'taken'
          ? 'Username is already taken'
          : localError;

  return (
    <div
      className="modal open"
      role="dialog"
      aria-modal="true"
      aria-labelledby="username-setup-title"
    >
      <div className="modal__box">
        <div className="modal__h">
          <span id="username-setup-title">Choose a username</span>
        </div>
        <form onSubmit={onSubmit}>
          <div className="modal__b">
            <p className="modal__s">
              Pick a unique handle for your profile. You can change display name later — this is
              your username.
            </p>
            <div className="fld" style={{ marginBottom: 0 }}>
              <label htmlFor="setup-username">Username</label>
              <input
                id="setup-username"
                className="inp"
                type="text"
                autoComplete="username"
                autoFocus
                maxLength={24}
                placeholder="e.g. cryptoverse_creator"
                value={username}
                disabled={busy || status === 'pending'}
                onChange={(e) => {
                  setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''));
                  setLocalError('');
                }}
              />
              {statusHint ? (
                <p
                  className="prof-hint"
                  style={{
                    marginTop: 10,
                    color:
                      availability === 'available'
                        ? 'var(--jade)'
                        : availability === 'taken' || localError
                          ? 'var(--oni)'
                          : undefined,
                  }}
                >
                  {statusHint}
                </p>
              ) : null}
            </div>
          </div>
          <div className="modal__f">
            <button
              type="submit"
              className="btn btn--go"
              disabled={
                busy ||
                status === 'pending' ||
                !username.trim() ||
                availability === 'taken' ||
                availability === 'checking'
              }
            >
              {busy || status === 'pending' ? 'Saving…' : 'Continue'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsernameSetupModal;
